import PropTypes from 'prop-types';
import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import ActivityStatus from '../../enums/ActivityStatus';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Edit, OpenInNew, Visibility, Link as LinkIcon, Info, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import CompanyRole from '../../enums/CompanyRole';

const MainActivityLinks = ({
    activity,
    companyRole,
    isBilledRequestPending,
    onLinksBtnClick,
    onSetupBtnClick,
    onViewStatusBtnClick,
    onSetIsBilledButtonClick
}) => {
    const { t } = useTranslation();
    const moderatorLink = activity.links?.moderatorLink;
    const status = activity.details.detailedStatus;
    const adminLink = activity.links?.adminLink;
    const staffObserverLink = activity.links?.staffObserverLink;
    const isBilled = activity.details.isBilled;

    const activityStatus = ActivityStatus[status];

    // Return a string describing why the moderator link button ('View Content / Edit Activity Content')
    // is disabled.
    const getModeratorLinkText = () => {
        if (!moderatorLink && !adminLink) {
            return t('activityPage.text.notModeratorOfThisActivity');
        } else if (!activityStatus.canLinkToActivity) {
            return 'Setup must be completed';
        } else {
            return '';
        }
    };

    const isOpenSessionButtonDisabled = () => {
        return getModeratorLinkText() !== '';
    };

    const OpenSessionButton = () => {
        return (
            <Tooltip title={getModeratorLinkText()} arrow>
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<OpenInNew />}
                        style={{ justifyContent: 'flex-start' }}
                        disabled={isOpenSessionButtonDisabled()}
                        onClick={() => {
                            if (moderatorLink) {
                                window.open(moderatorLink, '_blank');
                            } else if (adminLink) {
                                window.open(adminLink, '_blank');
                            }
                        }}
                    >
                        Enter Session
                    </Button>
                </div>
            </Tooltip>
        );
    };

    const ObserveButton = () => {
        if (!staffObserverLink) {
            return null;
        }

        return (
            <Button
                variant="outlined"
                fullWidth
                startIcon={<Visibility />}
                style={{ justifyContent: 'flex-start' }}
                onClick={() => window.open(staffObserverLink, '_blank')}
            >
                Observe
            </Button>
        );
    };

    const LinksButton = () => {
        // Always show links to vlStaff, otherwise only show for relevant activity statuses.
        if (!activityStatus.shouldShowLinks && !CompanyRole[companyRole].canAlwaysViewActivityLinks) {
            return null;
        }

        return (
            <Button
                variant="outlined"
                fullWidth
                startIcon={<LinkIcon />}
                style={{ justifyContent: 'flex-start' }}
                onClick={onLinksBtnClick}
            >
                Links
            </Button>
        );
    };

    const SetupButton = () => {
        if (CompanyRole[companyRole].canSetupActivity === false) {
            return null;
        }

        // Don't show the setup button for the following statuses.
        var excludedStatuses = [
            ActivityStatus.draft,
            ActivityStatus.completed,
            ActivityStatus.didNotStart
        ];

        if (excludedStatuses.includes(activityStatus)) {
            return null;
        }

        return (
            <Button
                variant="outlined"
                fullWidth
                startIcon={<Edit />}
                style={{ justifyContent: 'flex-start' }}
                onClick={onSetupBtnClick}
            >
                Setup
            </Button>
        );
    };

    const ViewParticipantStatusButton = () => {
        if (CompanyRole[companyRole] !== CompanyRole.vlStaff) {
            return null;
        }

        return (
            <Button
                variant="outlined"
                fullWidth
                style={{ justifyContent: 'flex-start' }}
                startIcon={<Info />}
                onClick={onViewStatusBtnClick}
            >
                { t('activityPage.text.participantStatus') }
            </Button>
        );
    };

    const SetIsBilledButton = () => {
        if (CompanyRole[companyRole].canSetActivityBilled === false) {
            return null;
        }
        let buttonIcon = null;
        if (isBilledRequestPending) {
            buttonIcon = <CircularProgress size={20} color="inherit" />;
        } else if (isBilled) {
            buttonIcon = <CheckBox />;
        } else {
            buttonIcon = <CheckBoxOutlineBlank />;
        }

        return (
            <Tooltip
                title={isBilled ? t('activityPage.tooltips.setIsBilledToFalse') : t('activityPage.tooltips.setIsBilledToTrue')}
                enterDelay={500}
                arrow
            >
                <div>
                    <Button
                        variant="outlined"
                        fullWidth
                        style={{ justifyContent: 'flex-start' }}
                        startIcon={buttonIcon}
                        onClick={onSetIsBilledButtonClick}
                        disabled={isBilledRequestPending}
                    >
                        { isBilled ?
                            t('activityPage.buttons.setIsBilledToFalse') :
                            t('activityPage.buttons.setIsBilledToTrue') }
                    </Button>
                </div>
            </Tooltip>
        );
    };

    const StaffButtons = () => {
        if (CompanyRole[companyRole] !== CompanyRole.vlStaff) {
            return null;
        }

        return (
            <React.Fragment>
                <Typography variant="caption" sx={{ paddingTop: 2 }}>
                    { t('common.text.vlStaffTools') }:
                </Typography>
                <ViewParticipantStatusButton />
                <SetIsBilledButton />
            </React.Fragment>
        );
    };

    return (
        <Stack gap={1}>
            <OpenSessionButton />

            <ObserveButton />

            <LinksButton />

            <SetupButton />

            <StaffButtons />
        </Stack>
    );
};

MainActivityLinks.propTypes = {
    activity: PropTypes.shape({
        details: PropTypes.shape({
            detailedStatus: PropTypes.any,
            isBilled: PropTypes.any
        }),
        links: PropTypes.shape({
            adminLink: PropTypes.any,
            moderatorLink: PropTypes.any,
            staffObserverLink: PropTypes.any
        })
    }),
    companyRole: PropTypes.any,
    isBilledRequestPending: PropTypes.bool,
    onSetIsBilledButtonClick: PropTypes.func,
    onLinksBtnClick: PropTypes.func,
    onSetupBtnClick: PropTypes.func,
    onViewStatusBtnClick: PropTypes.bool
};

export default MainActivityLinks;
